interface InvalidUsageErrorConfig {
  readonly className?: string;
  readonly methodName?: string;
}

const parseConfigMessage = (config: InvalidUsageErrorConfig): null | string => {
  if (config.methodName && config.className) {
    return `[${config.className}.${config.methodName}]`;
  } else if (config.className) {
    return `[${config.className}]`;
  } else if (config.methodName) {
    return `[${config.methodName}]`;
  }
  return null;
};

/**
 * An {@link Error} should be thrown when a method or class is used in an improper fashion.
 *
 * This {@link InvalidUsageError} is usually used in situations where the actual error should
 * not occur in runtime environments due to typings on the the method or class that should prevent
 * the misuse externally, but internally, TypeScript is unaware of these typings and cases where
 * the arguments are inconsistent with the expected function signatures need to be handled.
 */
export class InvalidUsageError extends Error {
  constructor(message?: string);
  constructor(message: string, config?: InvalidUsageErrorConfig);
  constructor(config?: InvalidUsageErrorConfig);

  constructor(arg0?: InvalidUsageErrorConfig | string, arg1?: InvalidUsageErrorConfig) {
    if (typeof arg0 === 'string') {
      const configMessage = parseConfigMessage(arg1 ?? {});
      if (configMessage) {
        super(`Invalid Usage ${configMessage}: ${arg0}`);
      } else {
        super(`Invalid Usage: ${arg0}`);
      }
    } else {
      const configMessage = parseConfigMessage(arg0 ?? {});
      if (configMessage) {
        super(`Invalid Usage ${configMessage}`);
      } else {
        super('Invalid Usage');
      }
    }
  }
}

/**
 * An {@link Error} that should be thrown when an unreachable code path is detected.
 */
export class UnreachableCaseError extends Error {
  constructor(message?: string) {
    super(message ? `Unreachable Case: ${message}` : 'Unreachable Case');
  }
}
